/**
 * "Where am I?" for the one-tap arrival estimate.
 *
 * Device-local on purpose: this is a preference, not tenant data, and promoting it
 * to the user record would mean a schema change, an endpoint and a migration for
 * something the browser can hold perfectly well.
 *
 * Note that geolocation is frequently NOT available here. The production stack
 * serves plain HTTP on port 8080, and `navigator.geolocation` requires a secure
 * context — so on `http://192.168.x.x:8080`, which is how an EOC actually reaches
 * this, the browser refuses. Picking an existing asset is the path that always
 * works, and on those deployments it is the normal one, not a fallback.
 */

import type { Asset } from '@ww/shared';

export type HomeSource = 'geolocation' | 'asset' | 'map' | 'tenant';

export interface HomePoint {
  lon: number;
  lat: number;
  label: string;
  source: HomeSource;
  assetId?: string;
  setAt: string;
}

const KEY = (tenantId: string) => `ww.home.${tenantId}`;

function isValid(p: unknown): p is HomePoint {
  const c = p as HomePoint | null;
  return (
    !!c &&
    typeof c.lon === 'number' &&
    typeof c.lat === 'number' &&
    Number.isFinite(c.lon) &&
    Number.isFinite(c.lat) &&
    Math.abs(c.lat) <= 90 &&
    Math.abs(c.lon) <= 180 &&
    typeof c.label === 'string'
  );
}

export function getHome(tenantId: string | null | undefined): HomePoint | null {
  if (!tenantId) return null;
  try {
    const raw = window.localStorage.getItem(KEY(tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isValid(parsed) ? parsed : null;
  } catch {
    // Private browsing, blocked storage, corrupt JSON — all the same to us.
    return null;
  }
}

export function setHome(tenantId: string | null | undefined, p: HomePoint): void {
  if (!tenantId) return;
  try {
    window.localStorage.setItem(KEY(tenantId), JSON.stringify(p));
  } catch {
    /* storage unavailable; the point still works for this session */
  }
}

export function clearHome(tenantId: string | null | undefined): void {
  if (!tenantId) return;
  try {
    window.localStorage.removeItem(KEY(tenantId));
  } catch {
    /* nothing to do */
  }
}

/** False on plain HTTP, in old browsers, and anywhere the API is missing. */
export function canUseGeolocation(): boolean {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator && window.isSecureContext;
}

export function requestGeolocation(timeoutMs = 10_000): Promise<HomePoint> {
  return new Promise((resolve, reject) => {
    if (!canUseGeolocation()) {
      reject(new Error('Location is not available on this connection.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lon: pos.coords.longitude,
          lat: pos.coords.latitude,
          label: 'your location',
          source: 'geolocation',
          setAt: new Date().toISOString(),
        }),
      (err) =>
        reject(
          new Error(
            err.code === err.PERMISSION_DENIED
              ? 'Location permission was declined.'
              : 'Could not get your location.',
          ),
        ),
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 300_000 },
    );
  });
}

export function homeFromAsset(asset: Asset): HomePoint {
  const [lon, lat] = asset.location.coordinates;
  return { lon, lat, label: asset.name, source: 'asset', assetId: asset._id, setAt: new Date().toISOString() };
}

/**
 * The county centre, used only until the user picks something real. Labelled as
 * the county so it is never mistaken for the user's own position.
 */
export function homeFromTenant(center: [number, number] | null | undefined, label: string): HomePoint | null {
  if (!center || typeof center[0] !== 'number' || typeof center[1] !== 'number') return null;
  return { lon: center[0], lat: center[1], label, source: 'tenant', setAt: new Date().toISOString() };
}

/** The asset nearest the tenant centre, to pre-select in the chooser. */
export function nearestAsset(assets: Asset[], center: [number, number] | null | undefined): Asset | null {
  const enabled = assets.filter((a) => a.enabled);
  if (!enabled.length) return null;
  if (!center) return enabled[0]!;
  let best = enabled[0]!;
  let bestD = Infinity;
  for (const a of enabled) {
    const [lon, lat] = a.location.coordinates;
    const d = (lon - center[0]) ** 2 + (lat - center[1]) ** 2;
    if (d < bestD) {
      bestD = d;
      best = a;
    }
  }
  return best;
}
